/**
 * Singleton onboarding service (API + reactive state).
 *
 * @author  Vikram Singh <vikram@maropost.com>
 * @created 2026
 */

import { ref, computed } from 'vue'
import type { AxiosResponse } from 'axios'
import { useUserStore } from '@/lib/stores/userStore'
import { UnificationHttpClient } from '@/lib/repositories'
import {
  ONBOARDING_COMPLETED_KEY,
  ONBOARDING_VALID_STATUSES,
} from '@/lib/constants/onboarding'
import type { OnboardingStatus, ProductAccess } from '@/lib/types/onboarding'
import {
  getAllOnboardingStepsFiltered,
  getAllOnboardingStepsFilteredSync,
  getProductAccessWithDefaults,
  preloadOnboardingStepsForEnabledProducts,
} from '@/lib/utils/loadOnboardingSteps'

type ProductAccessGetterHost = {
  getProductAccess: (productName: string) => boolean
  getProductAccessOnRoles: (productName: string) => boolean
}

const hasProductAccessWithRoles = (
  store: ProductAccessGetterHost,
  product: string
): boolean =>
  store.getProductAccess(product) && store.getProductAccessOnRoles(product)

/**
 * Resolve the product access for the onboarding service based on the user's roles.
 */
const resolveOnboardingProductAccess = (
  store: ProductAccessGetterHost
): ProductAccess =>
  getProductAccessWithDefaults({
    marketing: hasProductAccessWithRoles(store, 'marketing'),
    service: hasProductAccessWithRoles(store, 'service'),
    commerce: hasProductAccessWithRoles(store, 'commerce'),
  })
/**
 * Build step-id map from API when federated remotes did not return a catalog.
 */
const stepsFromItemsFallback = (
  data: Record<string, string>
): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const key of Object.keys(data)) {
    if (key !== ONBOARDING_COMPLETED_KEY) {
      out[key] = key
    }
  }
  return out
}

/**
 * Onboarding service
 */
const OnBoardingService = () => {
  const httpClient = UnificationHttpClient
  const items = ref<Record<string, string>>({})
  const enabledStepCatalog = ref<Record<string, string>>({})
  const isCompleted = ref(true)
  const isFetching = ref(false)
  const isUpdating = ref(false)

  /**
   * Step catalog for UI counts: reactive snapshot, else MF cache, else API keys.
   */
  const resolveEnabledSteps = (productAccess: ProductAccess): Record<string, string> => {
    if (Object.keys(enabledStepCatalog.value).length > 0) {
      return enabledStepCatalog.value
    }
    const sync = getAllOnboardingStepsFilteredSync(productAccess)
    if (Object.keys(sync).length > 0) {
      return sync
    }
    return stepsFromItemsFallback(items.value)
  }

  /**
   * Get the onboarding resource for the user.
   */
  const getOnboardingResource = async (): Promise<string> => {
    const userStore = useUserStore()
    return `/accounts/${userStore.getAccountId}/onboarding`
  }

  const validateStatus: (value: string) => asserts value is OnboardingStatus = (
    value
  ) => {
    if (!ONBOARDING_VALID_STATUSES.includes(value as OnboardingStatus)) {
      throw {
        data: {
          error: 'Invalid status value.',
        },
      } as AxiosResponse<{ error: string }>
    }
  }

  /**
   * Fetch the onboarding data from the API.
   */
  const fetchFromApi = async (
    config: Record<string, unknown> = {}
  ): Promise<AxiosResponse<any>> => {
    const onboardingResource = await getOnboardingResource()
    const response = await httpClient.get<any>(onboardingResource, config)
    const payload = response.data
    const onboardingData = payload?.data || {}
    const { [ONBOARDING_COMPLETED_KEY]: _ignored, ...onboardingConfigsObject } =
      onboardingData
    items.value = onboardingConfigsObject
    return { data: onboardingData } as AxiosResponse<any>
  }

  /**
   * Update onboarding data for a specific step.
   */
  const postOnboardingUpdate = async (
    key: string,
    value: string,
    config: Record<string, unknown> = {}
  ): Promise<AxiosResponse<any>> => {
    if (!key?.trim()) {
      throw {
        data: { error: 'Key is required' },
      } as AxiosResponse<{ error: string }>
    }

    validateStatus(value)

    const onboardingResource = await getOnboardingResource()
    const response = await httpClient.post<any>(
      onboardingResource,
      { data: { [key]: value } },
      config
    )

    if (response.data?.data) {
      if (key !== ONBOARDING_COMPLETED_KEY) {
        items.value[key] = value
      }
    }

    return response
  }

  /**
   * Computed property for the onboarding data.
   */
  const onboardingData = computed(() => items.value)

  const getOnboardingCompletedSteps = computed(() => {
    const store = useUserStore() as ProductAccessGetterHost
    const productAccess = resolveOnboardingProductAccess(store)
    const enabledSteps = resolveEnabledSteps(productAccess)
    const enabledStepsSet = new Set(Object.keys(enabledSteps))

    return Object.entries(items.value).filter(
      ([key, value]) =>
        key !== ONBOARDING_COMPLETED_KEY &&
        value === 'completed' &&
        enabledStepsSet.has(key)
    ).length
  })

  const getOnboardingTotalSteps = computed(() => {
    const store = useUserStore() as ProductAccessGetterHost
    const productAccess = resolveOnboardingProductAccess(store)
    const allSteps = resolveEnabledSteps(productAccess)
    return Object.keys(allSteps).length ?? 0
  })

  const getAllOnboardingStepKeys = computed(() => {
    const store = useUserStore() as ProductAccessGetterHost
    const productAccess = resolveOnboardingProductAccess(store)
    return resolveEnabledSteps(productAccess)
  })

  const getOnboardingCompletedPercentage = computed(() => {
    const store = useUserStore() as ProductAccessGetterHost
    const productAccess = resolveOnboardingProductAccess(store)
    const enabledSteps = resolveEnabledSteps(productAccess)
    const enabledStepsSet = new Set(Object.keys(enabledSteps))

    const completedSteps = Object.entries(items.value).filter(
      ([key, value]) =>
        key !== ONBOARDING_COMPLETED_KEY &&
        value === 'completed' &&
        enabledStepsSet.has(key)
    ).length

    const totalSteps = Object.keys(enabledSteps).length
    return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0
  })

  const isOnboardingCompleted = computed(() => isCompleted.value)

  const isOnboardingFetching = computed(() => isFetching.value)

  const isOnboardingUpdating = computed(() => isUpdating.value)

  /**
   * Get the onboarding data from the API.
   */
  const getOnboardingData = async (
    config: Record<string, unknown> = {}
  ): Promise<AxiosResponse<any>> => {
    const store = useUserStore() as ProductAccessGetterHost
    isFetching.value = true
    try {
      const productAccess = resolveOnboardingProductAccess(store)
      await preloadOnboardingStepsForEnabledProducts(productAccess)

      const response = await fetchFromApi(config)
      const onboardingPayload = response.data

      isCompleted.value =
        onboardingPayload[ONBOARDING_COMPLETED_KEY] === 'completed'

      const allSteps = await getAllOnboardingStepsFiltered(productAccess)
      const catalog =
        Object.keys(allSteps).length > 0
          ? allSteps
          : stepsFromItemsFallback(items.value)
      enabledStepCatalog.value = { ...catalog }

      const allStepsCompleted = Object.keys(catalog).every(
        (stepKey) => items.value[stepKey] === 'completed'
      )

      /**
       * Nav + access gating use the top-level `onboarding` key only — not “all steps done”.
       * Users finish steps first (`in_progress`), then visit Get Started once for Congratulations
       * before `onboarding` becomes `completed`.
       */

      if (
        !allStepsCompleted &&
        onboardingPayload[ONBOARDING_COMPLETED_KEY] === 'completed'
      ) {
        await postOnboardingUpdate(ONBOARDING_COMPLETED_KEY, 'in_progress')
        isCompleted.value = false
      }

      return response
    } catch (error) {
      console.error('[onBoardingService] Error fetching onboarding data:', error)
      items.value = {}
      enabledStepCatalog.value = {}
      throw error
    } finally {
      isFetching.value = false
    }
  }

  /**
   * Update onboarding data for a specific step.
   */
  const updateOnboardingData = async (
    key: string,
    value: string,
    config: Record<string, unknown> = {}
  ): Promise<AxiosResponse<any>> => {
    const store = useUserStore() as ProductAccessGetterHost
    /**
     * If the step is already completed, return a success response.
     */
    if (items.value[key] && items.value[key] === 'completed') {
      return Promise.resolve({
        data: { success: true },
      } as AxiosResponse<{ success: boolean }>)
    }

    /**
     * If the step is the completed key and the value is completed, return a success response.
     * This is to handle the case where the onboarding is already completed.
     */
    if (key === ONBOARDING_COMPLETED_KEY && value === 'completed') {
      const productAccess = resolveOnboardingProductAccess(store)
      const allSteps = await getAllOnboardingStepsFiltered(productAccess)
      const allStepKeys = Object.keys(allSteps)
      const allStepsCompleted = allStepKeys.every(
        (stepKey) => items.value[stepKey] === 'completed'
      )

      if (!allStepsCompleted) {
        throw {
          data: { error: 'All onboarding steps must be completed first' },
          status: 400,
          statusText: 'Bad Request',
          headers: {
            'Content-Type': 'application/json',
          },
        } as AxiosResponse<{ error: string }>
      }
    }

    isUpdating.value = true
    try {
      const response = await postOnboardingUpdate(key, value, config)

      /**
       * If the step is not the completed key and the completed key is completed, update the completed key to in_progress.
       * This is to handle the case where the onboarding is already completed.
       */
      if (
        key !== ONBOARDING_COMPLETED_KEY &&
        response?.data?.data?.[ONBOARDING_COMPLETED_KEY] === 'completed'
      ) {
        await postOnboardingUpdate(ONBOARDING_COMPLETED_KEY, 'in_progress')
        isCompleted.value = false
      }

      items.value[key] = value

      /**
       * If the step is the completed key, update the completed key to the value if the value is completed.
       * This is to handle the case where the onboarding is not completed.
       */
      if (key === ONBOARDING_COMPLETED_KEY) {
        isCompleted.value = value === 'completed'
      } else {
        const completionFromResponse =
          response?.data?.data?.[ONBOARDING_COMPLETED_KEY]
        if (completionFromResponse != null) {
          isCompleted.value = completionFromResponse === 'completed'
        }
      }

      return response
    } catch (error) {
      console.error('Error updating onboarding data:', error)
      throw error
    } finally {
      isUpdating.value = false
    }
  }

  /**
   * Reset the onboarding data.
   */
  const reset = (): void => {
    items.value = {}
    enabledStepCatalog.value = {}
    isCompleted.value = false
    isFetching.value = false
    isUpdating.value = false
  }

  return {
    items,
    onboardingData,
    isCompleted,
    isOnboardingCompleted,
    isOnboardingFetching,
    isOnboardingUpdating,
    getOnboardingCompletedSteps,
    getOnboardingTotalSteps,
    getAllOnboardingStepKeys,
    getOnboardingCompletedPercentage,
    getOnboardingData,
    updateOnboardingData,
    reset,
  }
}

export const onBoardingService = OnBoardingService()
